import { z } from 'zod';
/** Evidence is attributed to frozen source or trusted observations, never learner claims. */
export const fieldReviewEvidenceSchema = z.strictObject({
    kind: z.enum(['source', 'test', 'browser', 'requirement']),
    referenceId: z.string().max(240),
    path: z.string().max(240).nullable(),
    startLine: z.number().int().positive().nullable(),
    endLine: z.number().int().positive().nullable(),
    explanation: z.string().min(1).max(4000),
});
export const fieldReviewFindingSchema = z.strictObject({
    id: z.string().min(1).max(120),
    title: z.string().min(1).max(200),
    category: z.enum([
        'defect',
        'missed_discovery',
        'clarification',
        'scope_change',
        'improvement',
        'prevention',
    ]),
    severity: z.enum(['critical', 'high', 'medium', 'low']),
    confidence: z.enum(['confirmed', 'likely', 'needs_verification']),
    requirementIds: z.array(z.string()).max(30),
    expected: z.string().max(4000),
    actual: z.string().max(4000),
    impact: z.string().max(4000),
    recommendation: z.string().max(4000),
    verification: z.string().max(4000),
    evidence: z.array(fieldReviewEvidenceSchema).min(1).max(20),
});
/** Provider output contains no authority to manufacture execution or award a grade. */
export const fieldReviewDraftSchema = z.strictObject({
    summary: z.string().min(1).max(8000),
    message: z.string().min(1).max(6000),
    verdict: z.enum(['approve', 'changes_requested', 'needs_clarification']),
    whatWorks: z
        .array(z.strictObject({
        summary: z.string().max(4000),
        evidence: z.array(fieldReviewEvidenceSchema).min(1).max(20),
    }))
        .max(40),
    requirements: z
        .array(z.strictObject({
        id: z.string().min(1).max(240),
        title: z.string().max(500),
        status: z.enum(['met', 'partly_met', 'unmet', 'unverified']),
        explanation: z.string().max(4000),
        evidence: z.array(fieldReviewEvidenceSchema).max(20),
    }))
        .max(200),
    findings: z.array(fieldReviewFindingSchema).max(100),
    previousFindings: z
        .array(z.strictObject({
        findingId: z.string().max(120),
        status: z.enum(['resolved', 'persists', 'unverified']),
        explanation: z.string().max(4000),
        evidence: z.array(fieldReviewEvidenceSchema).max(20),
    }))
        .max(100),
    limitations: z.array(z.string().max(2000)).max(40),
});
export const fieldReviewReportSchema = fieldReviewDraftSchema.extend({
    modelId: z.literal('gpt-6.1-sol'),
    promptVersion: z.string(),
    contextId: z.string(),
    revision: z.string(),
    generatedAt: z.string(),
    coverage: z.strictObject({
        filesReviewed: z.array(z.string()).max(120),
        filesTotal: z.number().int().nonnegative(),
        checksObserved: z.array(z.string()),
        runtimeVerified: z.boolean(),
        browserVerified: z.boolean(),
    }),
});
export const fieldReviewStageSchema = z.enum([
    'queued',
    'preparing',
    'checking',
    'reviewing',
    'synthesizing',
    'completed',
    'failed',
]);
